// Framework-agnostic SEO config, exported as `@infinitro/config/data`. The package's
// main entry imports `$app/environment` + `svelte/store` and only works inside a
// SvelteKit runtime; build tooling (the sites/public Vite plugin) runs in plain Node
// and can't import it — which is why brand URLs / provider / locale lists used to be
// copy-pasted there. This is the shared source: import from here, never re-declare, and
// keep it free of `$app/*` and `svelte/*`.
// `.js` extensions are required: this chain (here → brands → types) is loaded by Node's
// ESM loader via the vite.config path, which rejects extensionless relative imports.
import { getThemeSetting as flushThemeSetting } from './brands/flush.js';
import { getThemeSetting as fomoThemeSetting } from './brands/fomo.js';
import { getThemeSetting as lunaThemeSetting } from './brands/luna.js';
import { getThemeSetting as rocketbetThemeSetting } from './brands/rocket.js';
import { getThemeSetting as turboslotsThemeSetting } from './brands/turbo.js';
// ─── Locales ─────────────────────────────────────────────────────────────────
/** All 17 locales the public sites can render. */
export const SEO_LOCALES = [
    'en',
    'es',
    'fr',
    'de',
    'it',
    'pt',
    'ru',
    'tr',
    'ar',
    'zh',
    'ja',
    'ko',
    'cs',
    'id',
    'ms',
    'th',
    'vi'
];
/**
 * The subset of locales flush.com ships full localized variants for (used to build
 * hreflang alternates and locale shells). Excludes `en` — English is the canonical.
 */
export const FLUSH_LOCALE_LANGS = [
    'cs',
    'de',
    'es',
    'fr',
    'it',
    'ja',
    'ko',
    'pt',
    'ru',
    'zh'
];
/**
 * Routes where flush.com has full locale variants and therefore emits the complete
 * hreflang network (x-default + en + FLUSH_LOCALE_LANGS). Must match the runtime
 * HREFLANG_ROUTES consumed in `sites/public/src/routes/+layout.svelte`.
 */
export const FLUSH_HREFLANG_ROUTES = [
    '/',
    '/casino',
    '/sports',
    '/livecasino',
    '/slots',
    '/vip',
    '/welcome-offer',
    '/referral',
    '/promotion-page'
];
// ─── Sitemap providers ───────────────────────────────────────────────────────
/**
 * Approved provider slugs included in the casino sitemap and provider shells.
 * To add a provider: add its slug here (the external catalog pipeline consumes this list).
 */
export const SITEMAP_PROVIDERS = [
    'avatarux',
    'bgaming',
    'blueprint-gaming',
    'boominggames',
    'fantasma',
    'flush-originals',
    'hacksaw-gaming',
    'pgsoft',
    'play-n-go',
    'pragmatic-play',
    'pragmatic-play-live',
    'gamomat-games',
    'playson',
    'quickspin',
    'relax-gaming',
    'shadylady',
    'wazdan',
    'yggdrasil-gaming'
];
// ─── Per-brand SEO identity ──────────────────────────────────────────────────
// Keyed by SEO brand key (VITE_THEME) + sub-brand keys. canonicalBase/cdnBase derive
// from brands/*.ts (empty domain → base setting; kr.fomo.io via fomo's sub-brand
// branch). Only fomo_eu/youpi lack a brand config → explicit SEO-only URLs below.
const flushBase = flushThemeSetting('', '');
const fomoBase = fomoThemeSetting('', '');
const krFomoBase = fomoThemeSetting('kr.fomo.io', 'kr.fomo.io');
const lunaBase = lunaThemeSetting('', '');
const rocketbetBase = rocketbetThemeSetting('', '');
const turboslotsBase = turboslotsThemeSetting('', '');
const TILES_BASE = {
    flush: flushBase.cdnTiles,
    fomo: fomoBase.cdnTiles,
    rocketbet: rocketbetBase.cdnTiles,
    luna: lunaBase.cdnTiles,
    turboslots: turboslotsBase.cdnTiles,
    'kr.fomo.io': krFomoBase.cdnTiles
};
const COMPANY_NAMES = {
    flush: 'Flush Crypto Casino',
    fomo: 'Fomo Crypto Casino',
    fomo_eu: 'Fomo EU Crypto Casino',
    rocketbet: 'Rocketbet Crypto Casino',
    youpi: 'Youpi Crypto Casino',
    luna: 'Luna Crypto Casino',
    turboslots: 'Turboslots Crypto Casino'
};
const SITE_NAMES = {
    flush: 'Flush',
    fomo: 'Fomo Crypto Casino',
    fomo_eu: 'Fomo EU Crypto Casino',
    rocketbet: 'Rocketbet',
    youpi: 'Youpi Casino',
    luna: 'Lunabet',
    turboslots: 'Turboslots',
    'kr.fomo.io': 'FOMO Casino'
};
// Youpi's og image lives on a different CDN than cdnBase.
const OG_IMAGE_OVERRIDES = {
    youpi: 'https://cdn.youpicasino.com/images/og/og-image.jpg',
    'kr.fomo.io': 'https://kr.fomo.io/assets/fomo-thumbnail.jpg'
};
const CANONICAL_BASE = {
    flush: flushBase.canonicalUrl,
    fomo: fomoBase.canonicalUrl,
    fomo_eu: 'https://eu.fomo.io', // SEO-only: no brand config
    rocketbet: rocketbetBase.canonicalUrl,
    youpi: 'https://youpicasino.com', // SEO-only: no brand config
    luna: lunaBase.canonicalUrl,
    turboslots: turboslotsBase.canonicalUrl,
    'kr.fomo.io': krFomoBase.canonicalUrl
};
const CDN_BASE = {
    flush: flushBase.cdnBase,
    fomo: fomoBase.cdnBase,
    fomo_eu: 'https://cdn.fomo.io', // SEO-only: no brand config
    rocketbet: rocketbetBase.cdnBase,
    youpi: 'https://gmcdn.org', // SEO-only: no brand config
    luna: lunaBase.cdnBase,
    turboslots: turboslotsBase.cdnBase,
    'kr.fomo.io': krFomoBase.cdnBase
};
const SAME_AS = {
    flush: [
        'https://x.com/FlushCasino',
        'https://discord.gg/NMsaxDF3Yu',
        'https://t.me/FlushOfficial',
        'https://www.instagram.com/flushcasinos',
        'https://www.linkedin.com/company/flush-com'
    ],
    'kr.fomo.io': ['https://www.facebook.com/fomo-casino', 'https://twitter.com/fomo-casino']
};
/** Resolved SEO identity for a brand, consumed when generating shells and JSON-LD. */
// The catalogue `rtp` field holds the **demo** configuration for these providers, not the
// return real-money play runs. Flush Originals are the known case: the catalogue reports
// 92%-99.43% per game while every Original runs 98% with real money, confirmed with the
// product team (the GameTech operator sheet carries the demo figure too, so neither
// machine-readable source has the real one). Publishing the catalogue value for these would
// state the demo return as though it applied to a deposited session.
export const REAL_MONEY_RTP_BY_PROVIDER = {
    'flush-originals': '98'
};
/**
 * Resolve the SEO identity for a brand. Pure — takes theme/brand explicitly so it
 * can run in any environment (the Vite plugin passes `process.env.VITE_THEME` /
 * `VITE_BRAND`; other callers pass their own values).
 */
export function resolveSeoBrandConfig(theme = 'flush', brand) {
    const key = brand ?? theme;
    const cdnBase = CDN_BASE[key] ?? CDN_BASE[theme] ?? CDN_BASE.flush;
    return {
        companyName: brand === 'kr.fomo.io' ? '포모 카지노' : (COMPANY_NAMES[theme] ?? COMPANY_NAMES.flush),
        canonicalBase: CANONICAL_BASE[key] ?? CANONICAL_BASE[theme] ?? CANONICAL_BASE.flush,
        cdnBase,
        tilesBase: TILES_BASE[key] ?? TILES_BASE[theme] ?? TILES_BASE.flush,
        siteName: SITE_NAMES[key] ?? SITE_NAMES[theme] ?? SITE_NAMES.flush,
        ogImageUrl: OG_IMAGE_OVERRIDES[key] ?? OG_IMAGE_OVERRIDES[theme] ?? `${cdnBase}/images/og/og-image.jpg`,
        ...(brand === 'kr.fomo.io' && { logoUrl: 'https://kr.fomo.io/assets/fomo-logo.png' }),
        ...(SAME_AS[key] && { sameAs: SAME_AS[key] })
    };
}
// Canonical origins used to brand-gate build logic (derived from the brand configs), so
// consumers compare against these instead of hardcoding `https://flush.com` etc.
export const SEO_CANONICAL = {
    flush: CANONICAL_BASE.flush,
    fomo: CANONICAL_BASE.fomo,
    krFomo: CANONICAL_BASE['kr.fomo.io']
};
/** True when the canonical origin is flush.com (the primary SEO brand). */
export function isFlushCanonical(canonicalBase) {
    return canonicalBase === SEO_CANONICAL.flush;
}
/** True when the canonical origin is in the fomo family (fomo.io or kr.fomo.io). */
export function isFomoFamilyCanonical(canonicalBase) {
    return canonicalBase === SEO_CANONICAL.fomo || canonicalBase === SEO_CANONICAL.krFomo;
}
// Crypto landing slugs published by the standalone seo-site Astro repo under /lp/en/<slug>/.
// Each landing links only to itself, so it needs an incoming link from flush.com to not be an
// orphan. Keep in sync with seo-site/src/content/landings/en/*.md (excluding `home`).
const LP_LANDING_SLUGS = [
    'bitcoin-baccarat',
    'bitcoin-casino',
    'bitcoin-crash',
    'bitcoin-dice',
    'bitcoin-live-casino',
    'bitcoin-poker',
    'bitcoin-roulette',
    'bitcoin-slots',
    'bnb-casino',
    'bnb-slots',
    'crypto-blackjack',
    'dogecoin-baccarat',
    'dogecoin-blackjack',
    'dogecoin-casino',
    'dogecoin-dice',
    'dogecoin-live-casino',
    'dogecoin-slots',
    'ethereum-baccarat',
    'ethereum-blackjack',
    'ethereum-casino',
    'ethereum-dice',
    'ethereum-live-casino',
    'ethereum-poker',
    'ethereum-roulette',
    'ethereum-slots',
    'instant-withdrawal-casino',
    'litecoin-baccarat',
    'litecoin-blackjack',
    'litecoin-casino',
    'litecoin-dice',
    'litecoin-live-casino',
    'litecoin-roulette',
    'litecoin-slots',
    'polygon-casino',
    'tron-casino',
    'tron-slots',
    'usdc-casino',
    'usdt-casino',
    'usdt-dice'
];
const titleCase = (slug) => slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
/**
 * Curated crawlable entry links from flush.com into the /lp/ SEO landing site (a separate repo
 * served under /lp/). Emitted on high-authority marketing shells so the landing pages and the
 * casino hub — which nothing on flush.com linked before — are reachable by crawlers. `path` is a
 * root-relative flush.com path; consumers prepend the canonical origin.
 */
export const LP_ENTRY_LINKS = [
    { path: '/lp/casino/', label: 'Casino Guides & Games' },
    ...LP_LANDING_SLUGS.map((slug) => ({ path: `/lp/en/${slug}/`, label: titleCase(slug) }))
];
